/**
 * Free Gift — multi-campaign auto-add
 *
 * window.__FREE_GIFT_CONFIG.campaigns[]:
 *   key, title, giftVariantId, giftProductId, type ('qty'|'spend'), ratio, minimumAmount, enabled
 *
 * Each campaign independently adds/removes its own $0 gift. Gift SKUs never count
 * toward another campaign's qty or spend.
 */
(function () {
  const CFG = window.__FREE_GIFT_CONFIG;
  if (!CFG || !Array.isArray(CFG.campaigns) || !CFG.campaigns.length || !window.Shopify?.routes?.root) return;

  const root = window.Shopify.routes.root;
  const GIFT_PROP = '_free_gift';
  const GIFT_KEY_PROP = '_free_gift_key';
  const GIFT_TITLE_PROP = '_free_gift_title';
  const log = (...a) => { if (CFG.debug) console.log('[FreeGift]', ...a); };

  const campaigns = CFG.campaigns.filter((c) => c && c.key && c.giftVariantId);
  const giftVariantIdSet = new Set(campaigns.map((c) => c.giftVariantId));
  const giftProductIdSet = new Set(campaigns.map((c) => c.giftProductId).filter(Boolean));
  const promoInactive = Object.fromEntries(campaigns.map((c) => [c.key, false]));

  let isReconciling = false;
  let timer = null;

  const isCartPage = () => window.themeVariables?.settings?.pageType === 'cart';
  const getCart = async () => (await fetch(root + 'cart.js')).json();

  function isManagedGift(item) {
    return !!(item.properties && item.properties[GIFT_PROP]);
  }

  function isAnyGiftSku(item) {
    return giftVariantIdSet.has(item.variant_id) || giftProductIdSet.has(item.product_id);
  }

  function isGiftLineFor(item, campaign) {
    if (!isManagedGift(item)) return false;
    if (item.properties[GIFT_KEY_PROP]) return item.properties[GIFT_KEY_PROP] === campaign.key;
    return item.variant_id === campaign.giftVariantId;
  }

  function isQualifyingItem(item) {
    if (isManagedGift(item)) return false;
    if (isAnyGiftSku(item)) return false;
    return true;
  }

  function paidQty(cart) {
    let qty = 0;
    for (const item of cart.items) {
      if (isQualifyingItem(item)) qty += item.quantity;
    }
    return qty;
  }

  function paidSubtotal(cart) {
    let total = 0;
    for (const item of cart.items) {
      if (isQualifyingItem(item)) total += item.final_line_price;
    }
    return total;
  }

  function desiredQty(campaign, cart) {
    if (campaign.enabled === false) return 0;
    if (campaign.type === 'spend') {
      return paidSubtotal(cart) >= (campaign.minimumAmount || 0) ? 1 : 0;
    }
    return paidQty(cart) * (campaign.ratio || 1);
  }

  function collectSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections.join(',');
  }

  async function mutate(endpoint, body) {
    const res = await fetch(root + endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
    if (!res.ok) console.warn('[FreeGift] cart API error', endpoint, res.status);
    return res.json();
  }

  async function renderCart() {
    return mutate('cart/update.js', { updates: {}, sections: collectSections() });
  }

  function dispatchChange(cart, baseEvent) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent, cart } })
    );
  }

  async function rawSetGiftQty(campaign, giftLine, qty) {
    if (giftLine) return mutate('cart/change.js', { id: giftLine.key, quantity: qty });
    if (qty <= 0) return getCart();
    await mutate('cart/add.js', {
      items: [{
        id: campaign.giftVariantId,
        quantity: qty,
        properties: {
          [GIFT_PROP]: 'true',
          [GIFT_KEY_PROP]: campaign.key,
          [GIFT_TITLE_PROP]: campaign.title || ''
        }
      }]
    });
    return getCart();
  }

  async function syncGifts(cart) {
    let changed = false;

    for (const item of cart.items) {
      if (isAnyGiftSku(item) && !isManagedGift(item)) {
        await mutate('cart/change.js', { id: item.key, quantity: 0 });
        changed = true;
      }
    }
    if (changed) cart = await getCart();

    const qty = paidQty(cart);
    const subtotal = paidSubtotal(cart);
    if (qty === 0 && subtotal === 0) {
      campaigns.forEach((c) => { promoInactive[c.key] = false; });
    }

    for (const campaign of campaigns) {
      const giftLine = cart.items.find((it) => isGiftLineFor(it, campaign));
      const desired = desiredQty(campaign, cart);
      const current = giftLine ? giftLine.quantity : 0;
      log(campaign.key, { desired, current, qty, subtotal });

      if (desired === 0) promoInactive[campaign.key] = false;

      if (desired !== current && !(promoInactive[campaign.key] && desired > current)) {
        await rawSetGiftQty(campaign, giftLine, desired);
        changed = true;
        cart = await getCart();
      }
    }

    const after = changed ? cart : await getCart();
    for (const campaign of campaigns) {
      const gl = after.items.find((it) => isGiftLineFor(it, campaign));
      if (gl && gl.quantity > 0 && gl.final_line_price > 0) {
        promoInactive[campaign.key] = true;
        await rawSetGiftQty(campaign, gl, 0);
        changed = true;
      }
    }

    return changed;
  }

  async function refreshUI(baseEvent) {
    if (isCartPage()) { window.location.reload(); return; }
    const cart = await renderCart();
    if (cart && cart.sections) dispatchChange(cart, baseEvent || 'line-item:change');
  }

  async function reconcile() {
    if (isReconciling) return;
    isReconciling = true;
    try {
      const changed = await syncGifts(await getCart());
      if (changed) await refreshUI('line-item:change');
    } catch (e) {
      console.error('[FreeGift] reconcile error:', e);
    } finally {
      isReconciling = false;
    }
  }

  function schedule() {
    if (isReconciling) return;
    clearTimeout(timer);
    timer = setTimeout(reconcile, 80);
  }

  function readChangeIntent(event) {
    const link = event.type === 'click' && event.target.closest
      ? event.target.closest('a[href*="/cart/change"]') : null;
    if (link) {
      const url = new URL(link.href, window.location.origin);
      return { lineKey: url.searchParams.get('id'), quantity: parseInt(url.searchParams.get('quantity'), 10) };
    }
    if (event.type === 'change' && event.target.matches && event.target.matches('input[data-line-key]')) {
      return { lineKey: event.target.getAttribute('data-line-key'), quantity: parseInt(event.target.value, 10) };
    }
    return null;
  }

  function onCartPageChange(event) {
    const intent = readChangeIntent(event);
    if (!intent || !intent.lineKey || Number.isNaN(intent.quantity)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    applyCartPageChange(intent);
  }

  async function applyCartPageChange(intent) {
    isReconciling = true;
    try {
      await mutate('cart/change.js', { id: intent.lineKey, quantity: intent.quantity });
      await syncGifts(await getCart());
    } catch (e) {
      console.error('[FreeGift] cart-page sync error:', e);
    } finally {
      window.location.reload();
    }
  }

  let checkoutSyncing = false;
  async function onCheckoutClick(event) {
    const ctrl = event.target && event.target.closest
      ? event.target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]')
      : null;
    if (!ctrl || ctrl.__freeGiftReady) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutSyncing) return;
    checkoutSyncing = true;
    try { await syncGifts(await getCart()); }
    catch (e) { console.error('[FreeGift] pre-checkout sync error:', e); }
    finally {
      ctrl.__freeGiftReady = true;
      checkoutSyncing = false;
      if (ctrl.tagName === 'A') window.location.assign(ctrl.href);
      else ctrl.click();
    }
  }

  document.addEventListener('cart:change', schedule);
  document.addEventListener('click', onCheckoutClick, true);

  if (isCartPage()) {
    document.addEventListener('click', onCartPageChange, true);
    document.addEventListener('change', onCartPageChange, true);
  }

  if (document.readyState !== 'loading') schedule();
  else document.addEventListener('DOMContentLoaded', schedule);
})();
